'use client'

import { useEffect, useState } from 'react'
import { GetInviteFlow } from './GetInviteFlow'
import { activationTranslations, ActivationLocale } from '../translations/activation'

interface WaitlistPopoverProps {
  isOpen: boolean
  onClose: () => void
  anchorEl: HTMLElement | null
  locale?: string
  persona?: 'begin' | 'grow'
  accentColor?: string
}

export function WaitlistPopover({
  isOpen,
  onClose,
  locale = 'en',
  persona = 'begin',
  accentColor = '#C5ECAC',
}: WaitlistPopoverProps) {
  const [flowKey, setFlowKey] = useState(0)
  const isGrow = persona === 'grow'
  const t = activationTranslations[locale as ActivationLocale] || activationTranslations.en

  useEffect(() => {
    if (!isOpen) return

    const html = document.documentElement
    const body = document.body
    const originalHtmlOverflow = html.style.overflow
    const originalBodyOverflow = body.style.overflow
    const originalHtmlOverscroll = html.style.overscrollBehavior
    const originalBodyOverscroll = body.style.overscrollBehavior

    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    html.style.overscrollBehavior = 'none'
    body.style.overscrollBehavior = 'none'

    return () => {
      html.style.overflow = originalHtmlOverflow
      body.style.overflow = originalBodyOverflow
      html.style.overscrollBehavior = originalHtmlOverscroll
      body.style.overscrollBehavior = originalBodyOverscroll
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => setFlowKey(key => key + 1), 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-6 sm:px-6">
      <button
        type="button"
        aria-label={t.closeModalLabel}
        className="invite-overlay absolute inset-0 cursor-default"
        onClick={onClose}
        style={{
          top: 'calc(-1 * env(safe-area-inset-top, 0px))',
          bottom: 'calc(-1 * env(safe-area-inset-bottom, 0px))',
          backdropFilter: 'blur(16px) saturate(1.1)',
          WebkitBackdropFilter: 'blur(16px) saturate(1.1)',
        }}
      />

      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-popover-title"
        className="invite-card relative max-h-[calc(100dvh-2rem)] w-full max-w-[540px] overflow-y-auto rounded-[34px] p-7 sm:p-[42px]"
        style={{
          background: isGrow
            ? 'linear-gradient(160deg, rgba(214,233,247,0.80), rgba(240,246,250,0.66))'
            : 'linear-gradient(160deg, rgba(231,247,222,0.80), rgba(245,250,242,0.66))',
          backdropFilter: 'blur(44px) saturate(1.7)',
          WebkitBackdropFilter: 'blur(44px) saturate(1.7)',
          border: '1px solid rgba(255,255,255,0.55)',
          boxShadow: '0 40px 90px rgba(9,20,16,0.30), inset 0 1px 0 rgba(255,255,255,0.7)',
        }}
      >
        <div
          className="pointer-events-none absolute left-1/2 top-0 h-24 w-[68%] -translate-x-1/2 rounded-full"
          style={{ background: 'radial-gradient(ellipse at top, rgba(255,255,255,0.34), rgba(255,255,255,0) 70%)' }}
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-10 flex h-[38px] w-[38px] items-center justify-center rounded-full text-[#0c1a14] transition-transform hover:scale-105"
          style={{
            background: 'rgba(255,255,255,0.46)',
            border: '1px solid rgba(255,255,255,0.55)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }}
          aria-label={t.closeLabel}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="relative z-10">
          <GetInviteFlow key={flowKey} locale={locale} accentColor={accentColor} />
        </div>
      </section>
    </div>
  )
}
